import React from 'react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../../../components/ui/card';

/**
 * P&L Comparison Table Component
 * Displays detailed side-by-side comparison of financial metrics
 */
export const PLComparisonTable = ({ 
  profitLossData, 
  plCompareData, 
  calcVariance, 
  formatVariance 
}) => {
  if (!profitLossData || !plCompareData) return null;

  const comparisonRows = [
    { 
      label: 'Revenue (ex GST)', 
      current: profitLossData.income.revenue_ex_gst, 
      prev: plCompareData.income.revenue_ex_gst, 
      isProfit: true 
    },
    { 
      label: 'Cost of Sales', 
      current: (profitLossData.cost_of_goods_sold?.total_cost_of_sales || profitLossData.cost_of_goods_sold?.stock_cost + profitLossData.expenses.stock_purchases), 
      prev: (plCompareData.cost_of_goods_sold?.total_cost_of_sales || plCompareData.cost_of_goods_sold?.stock_cost + plCompareData.expenses.stock_purchases), 
      isProfit: false 
    },
    { 
      label: 'Gross Profit', 
      current: profitLossData.summary.gross_profit, 
      prev: plCompareData.summary.gross_profit, 
      isProfit: true, 
      highlight: true 
    },
    { 
      label: 'Operating Expenses', 
      current: profitLossData.expenses.expenses_ex_gst, 
      prev: plCompareData.expenses.expenses_ex_gst, 
      isProfit: false 
    },
    { 
      label: 'Net Profit', 
      current: profitLossData.summary.net_profit, 
      prev: plCompareData.summary.net_profit, 
      isProfit: true, 
      highlight: true 
    },
    { 
      label: 'Profit Margin', 
      current: profitLossData.summary.profit_margin, 
      prev: plCompareData.summary.profit_margin, 
      isProfit: true, 
      isPercent: true 
    },
    { 
      label: 'Job Count', 
      current: profitLossData.income.job_count, 
      prev: plCompareData.income.job_count, 
      isProfit: true, 
      isCount: true 
    },
  ];

  return (
    <Card style={{ border: '2px solid #e0f2fe', backgroundColor: '#f0f9ff' }}>
      <CardHeader>
        <CardTitle style={{ fontSize: '1rem', color: '#0369a1' }}>
          Period Comparison Summary
        </CardTitle>
        <CardDescription>
          Detailed comparison between current and previous period
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
            <thead>
              <tr style={{ backgroundColor: '#e0f2fe' }}>
                <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontWeight: '600', borderBottom: '2px solid #0ea5e9' }}>Metric</th>
                <th style={{ padding: '0.75rem 1rem', textAlign: 'right', fontWeight: '600', borderBottom: '2px solid #0ea5e9' }}>Current Period</th>
                <th style={{ padding: '0.75rem 1rem', textAlign: 'right', fontWeight: '600', borderBottom: '2px solid #0ea5e9' }}>Previous Period</th>
                <th style={{ padding: '0.75rem 1rem', textAlign: 'right', fontWeight: '600', borderBottom: '2px solid #0ea5e9' }}>Variance ($)</th>
                <th style={{ padding: '0.75rem 1rem', textAlign: 'right', fontWeight: '600', borderBottom: '2px solid #0ea5e9' }}>Variance (%)</th>
              </tr>
            </thead>
            <tbody>
              {comparisonRows.map((row, idx) => {
                const v = calcVariance(row.current, row.prev);
                const f = formatVariance(v, row.isProfit);
                return (
                  <tr key={idx} style={{ backgroundColor: row.highlight ? '#f0fdf4' : (idx % 2 === 0 ? 'white' : '#f8fafc') }}>
                    <td style={{ padding: '0.75rem 1rem', fontWeight: row.highlight ? '600' : '400', borderBottom: '1px solid #e2e8f0' }}>
                      {row.label}
                    </td>
                    <td style={{ padding: '0.75rem 1rem', textAlign: 'right', fontWeight: row.highlight ? '600' : '400', borderBottom: '1px solid #e2e8f0' }}>
                      {row.isPercent ? `${row.current.toFixed(1)}%` : row.isCount ? row.current : `$${row.current.toFixed(2)}`}
                    </td>
                    <td style={{ padding: '0.75rem 1rem', textAlign: 'right', color: '#6b7280', borderBottom: '1px solid #e2e8f0' }}>
                      {row.isPercent ? `${row.prev.toFixed(1)}%` : row.isCount ? row.prev : `$${row.prev.toFixed(2)}`}
                    </td>
                    <td style={{ padding: '0.75rem 1rem', textAlign: 'right', color: f.color, fontWeight: '500', borderBottom: '1px solid #e2e8f0' }}>
                      {f.arrow} {row.isPercent ? `${v.diff.toFixed(1)}pp` : row.isCount ? v.diff : f.diffText}
                    </td>
                    <td style={{ padding: '0.75rem 1rem', textAlign: 'right', color: f.color, fontWeight: '500', borderBottom: '1px solid #e2e8f0' }}>
                      {f.pctText}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
};

export default PLComparisonTable;
